import { useEffect, useState } from 'react';
import { operationsApi, paymentsApi, systemApi, type PaymentProvider } from '../lib/api';

const KIND_LABELS: Record<string, string> = {
  STRIPE: 'Stripe',
  PAYPAL: 'PayPal',
  MOBILE_MONEY: 'Agrégateur mobile money',
  SANDBOX: 'Bac à sable',
};

function money(minor: number, currency: string) {
  return `${(minor / 100).toFixed(2)} ${currency}`;
}

export function PaymentsAdminPage() {
  const [providers, setProviders] = useState<PaymentProvider[]>([]);
  const [payouts, setPayouts] = useState<any[]>([]);
  const [fees, setFees] = useState<any[]>([]);
  const [outbox, setOutbox] = useState<any[]>([]);
  const [form, setForm] = useState({
    code: '', kind: 'STRIPE', label: '', mode: 'SANDBOX',
    webhook_secret_env: '', api_key_env: '', api_base_url: '',
    return_url: '', cancel_url: '', country_code: '', currency: 'USD', supports_payout: false,
  });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => { reload(); }, []);

  async function reload() {
    try { setProviders(await paymentsApi.providers()); }
    catch (e: any) { setError(String(e.message || e)); }
    try { setPayouts(await paymentsApi.payouts()); }
    catch { /* réservé aux administrateurs financiers */ }
    try { setFees(await operationsApi.fees()); } catch { /* ignoré */ }
    try { setOutbox(await operationsApi.outbox()); } catch { /* ignoré */ }
  }

  async function register() {
    setError(''); setMessage('');
    try {
      await paymentsApi.registerProvider(form);
      setMessage('Prestataire enregistré.');
      await reload();
    } catch (e: any) { setError(String(e.message || e)); }
  }

  const liveWithoutSecret = form.mode === 'LIVE' && !form.webhook_secret_env.trim();

  return <section className="page">
    <header className="page-header">
      <div>
        <span className="eyebrow">PAIEMENTS</span>
        <h1>Prestataires et reversements</h1>
        <p>Les secrets ne sont jamais saisis ici : seul le nom de la variable d'environnement qui les porte est enregistré.</p>
      </div>
    </header>

    {error && <div className="alert error">{error}</div>}
    {message && <div className="notice">{message}</div>}

    <div className="grid two">
      <div className="card">
        <h3>Déclarer un prestataire</h3>
        <label>Code<input value={form.code} onChange={(e: any) => setForm({ ...form, code: e.target.value })} placeholder="STRIPE-US" /></label>
        <label>Type
          <select value={form.kind} onChange={(e: any) => setForm({ ...form, kind: e.target.value })}>
            {Object.entries(KIND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label>Libellé<input value={form.label} onChange={(e: any) => setForm({ ...form, label: e.target.value })} /></label>
        <label>Mode
          <select value={form.mode} onChange={(e: any) => setForm({ ...form, mode: e.target.value })}>
            <option value="SANDBOX">Bac à sable</option>
            <option value="LIVE">Production</option>
          </select>
        </label>
        <label>Variable du secret de webhook
          <input value={form.webhook_secret_env} onChange={(e: any) => setForm({ ...form, webhook_secret_env: e.target.value })} placeholder="STRIPE_WEBHOOK_SECRET" />
        </label>
        <label>Variable de la clé d'API
          <input value={form.api_key_env} onChange={(e: any) => setForm({ ...form, api_key_env: e.target.value })} placeholder="STRIPE_API_KEY" />
        </label>
        {form.kind === 'PAYPAL' && <p className="technical-note">Pour PayPal, la variable doit contenir <code>client_id:secret</code>.</p>}
        <label>URL de l'API (facultatif)
          <input value={form.api_base_url} onChange={(e: any) => setForm({ ...form, api_base_url: e.target.value })} placeholder="laisser vide pour l'URL officielle" />
        </label>
        <label>URL de retour<input value={form.return_url} onChange={(e: any) => setForm({ ...form, return_url: e.target.value })} /></label>
        <label>URL d'annulation<input value={form.cancel_url} onChange={(e: any) => setForm({ ...form, cancel_url: e.target.value })} /></label>
        {form.kind === 'MOBILE_MONEY' && <>
          <label>Pays servi<input value={form.country_code} onChange={(e: any) => setForm({ ...form, country_code: e.target.value })} placeholder="HT" /></label>
          <label>Devise<input value={form.currency} onChange={(e: any) => setForm({ ...form, currency: e.target.value })} /></label>
        </>}
        <label className="checkbox">
          <input type="checkbox" checked={form.supports_payout} onChange={(e: any) => setForm({ ...form, supports_payout: e.target.checked })} />
          Autorise les reversements
        </label>

        {liveWithoutSecret && <p className="technical-note">Un prestataire en production exige un secret de webhook : sans lui, aucun paiement ne peut être confirmé de façon sûre.</p>}
        <button className="button primary" disabled={!form.code.trim() || liveWithoutSecret} onClick={register}>Enregistrer</button>
      </div>

      <div className="card">
        <h3>Prestataires configurés</h3>
        {providers.map(p => <div key={p.id} className="order-row">
          <strong>{p.label || p.code} — {KIND_LABELS[p.kind] || p.kind}</strong>
          <span>
            {p.mode === 'LIVE' ? 'Production' : 'Bac à sable'}
            {p.country_code ? ` · ${p.country_code}` : ''} · {p.currency}
            {p.supports_payout ? ' · reversements' : ''}
          </span>
          <small>
            webhook {p.webhook_secret_configured ? 'configuré' : 'absent'} ·
            appels réels {(p as any).live_calls_enabled ? 'actifs' : 'hors ligne'}
          </small>
          {(p as any).last_error && <em className="technical-note">Dernière erreur : {(p as any).last_error}</em>}
        </div>)}
        {!providers.length && <p className="technical-note">Aucun prestataire configuré.</p>}

        <h3>Reversements</h3>
        {payouts.map(p => <div key={p.id} className="order-row">
          <strong>{money(p.amount_minor, p.currency)} → {p.beneficiary_user_id.slice(0, 8)}</strong>
          <span>{p.status}</span>
          {p.status === 'REQUESTED' && <>
            <small className="technical-note">L'approbateur doit différer du demandeur.</small>
            <button className="button primary" onClick={async () => {
              setError('');
              try { await systemApi.approvePayout(p.id); await reload(); }
              catch (e: any) { setError(String(e.message || e)); }
            }}>Approuver et exécuter</button>
          </>}
        </div>)}
        {!payouts.length && <p className="technical-note">Aucun reversement.</p>}
      </div>
    </div>

    <div className="grid two">
      <div className="card">
        <h3>Tarifs de plateforme</h3>
        <p className="technical-note">Modifiables à chaud. « défaut » signifie qu'aucune valeur propre n'a été fixée.</p>
        <table className="price-table">
          <thead><tr><th>Code</th><th>Montant</th><th>Origine</th><th /></tr></thead>
          <tbody>
            {fees.map(f => <tr key={f.code}>
              <td>{f.code}</td>
              <td>{money(f.amount_minor, f.currency)}</td>
              <td>{f.is_default ? 'défaut' : 'personnalisé'}</td>
              <td><button className="button secondary" onClick={async () => {
                const raw = window.prompt(`Nouveau montant en centimes pour ${f.code}`, String(f.amount_minor));
                if (!raw) return;
                setError('');
                try { await operationsApi.setFee(f.code, Number(raw)); await reload(); }
                catch (e: any) { setError(String(e.message || e)); }
              }}>Modifier</button></td>
            </tr>)}
            {!fees.length && <tr><td colSpan={4}>Tarifs non chargés.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3>Courriels sortants</h3>
        <p className="technical-note">
          Sans SMTP configuré, les messages portent le statut STORED : leur contenu
          reste ici, ce qui permet de débloquer un participant à la main.
        </p>
        {outbox.slice(0, 15).map(m => <div key={m.id} className="order-row">
          <strong>{m.to_address}</strong>
          <span>{m.subject} · {m.kind}</span>
          <small>{m.status}{m.error ? ` — ${m.error}` : ''} · {new Date(m.created_at).toLocaleString()}</small>
          {m.body && <em className="technical-note">{m.body.slice(0, 300)}</em>}
        </div>)}
        {!outbox.length && <p className="technical-note">Aucun courriel produit.</p>}
      </div>
    </div>
  </section>;
}
